import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faImage, faFile } from '@fortawesome/free-solid-svg-icons';
import {
  SHIPMENT_TYPE_LABELS,
  type IntakeShipmentType,
  type ShipmentAttachment,
} from '@/services/equipment-intake.service';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { SELECT_CLASS, SHIPMENT_TYPES, ATTACHMENT_MIME } from '../intake-form.constants';

export interface ShipmentState {
  id?: string;
  shipmentType: IntakeShipmentType;
  courierName: string;
  trackingNumber: string;
  dispatchDate: string;
  receivedDate: string;
  remarks: string;
  existingAttachments: ShipmentAttachment[];
  removedAttachmentIndices: number[];
  pendingAttachmentFiles: File[];
}

const MAX_SHIPMENT_ATTACHMENTS = 10;

interface ShipmentCardProps {
  index: number;
  state: ShipmentState;
  onChange: (state: ShipmentState) => void;
  onRemove: () => void;
  disabled: boolean;
}

/** Reused verbatim from the original EquipmentIntakePage modal form — extracted
 * only so the Equipment Intake Workspace (Feature 5) can reuse it as-is. */
export function ShipmentCard({ index, state, onChange, onRemove, disabled }: ShipmentCardProps) {
  const attachInputRef = useRef<HTMLInputElement>(null);
  const visibleExisting = state.existingAttachments.filter(
    (_, i) => !state.removedAttachmentIndices.includes(i),
  );
  const totalAttachments = visibleExisting.length + state.pendingAttachmentFiles.length;
  const set = (patch: Partial<ShipmentState>) => onChange({ ...state, ...patch });

  const handleAttachChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const toAdd = files.slice(0, MAX_SHIPMENT_ATTACHMENTS - totalAttachments);
    if (toAdd.length > 0)
      set({ pendingAttachmentFiles: [...state.pendingAttachmentFiles, ...toAdd] });
  };

  const showCourier = state.shipmentType === 'COURIER' || state.shipmentType === 'TRANSPORT';
  const showTracking = state.shipmentType === 'COURIER';

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Shipment {index + 1}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-destructive transition-colors"
            aria-label="Remove shipment"
          >
            <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Shipment Type" htmlFor={`sh-type-${index}`} required>
          <select
            id={`sh-type-${index}`}
            value={state.shipmentType}
            onChange={(e) => set({ shipmentType: e.target.value as IntakeShipmentType })}
            disabled={disabled}
            className={SELECT_CLASS}
          >
            {SHIPMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {SHIPMENT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </FormField>

        {showCourier && (
          <FormField label="Courier Name" htmlFor={`sh-courier-${index}`}>
            <Input
              id={`sh-courier-${index}`}
              value={state.courierName}
              onChange={(e) => set({ courierName: e.target.value })}
              placeholder="e.g. FedEx"
              disabled={disabled}
            />
          </FormField>
        )}

        {showTracking && (
          <FormField label="Tracking Number" htmlFor={`sh-track-${index}`}>
            <Input
              id={`sh-track-${index}`}
              value={state.trackingNumber}
              onChange={(e) => set({ trackingNumber: e.target.value })}
              placeholder="Waybill / tracking #"
              disabled={disabled}
            />
          </FormField>
        )}

        <FormField label="Dispatch Date" htmlFor={`sh-disp-${index}`}>
          <Input
            id={`sh-disp-${index}`}
            type="date"
            value={state.dispatchDate}
            onChange={(e) => set({ dispatchDate: e.target.value })}
            disabled={disabled}
          />
        </FormField>

        <FormField label="Received Date" htmlFor={`sh-recv-${index}`}>
          <Input
            id={`sh-recv-${index}`}
            type="date"
            value={state.receivedDate}
            onChange={(e) => set({ receivedDate: e.target.value })}
            disabled={disabled}
          />
        </FormField>
      </div>

      <FormField label="Remarks" htmlFor={`sh-rem-${index}`}>
        <Input
          id={`sh-rem-${index}`}
          value={state.remarks}
          onChange={(e) => set({ remarks: e.target.value })}
          placeholder="Optional notes"
          disabled={disabled}
        />
      </FormField>

      <div>
        <span className="text-xs text-muted-foreground">
          Documents ({totalAttachments}/{MAX_SHIPMENT_ATTACHMENTS})
        </span>
        {(visibleExisting.length > 0 || state.pendingAttachmentFiles.length > 0) && (
          <div className="mt-1.5 space-y-1">
            {state.existingAttachments.map((att, i) => {
              if (state.removedAttachmentIndices.includes(i)) return null;
              const name = att.url.split('/').pop()?.split('?')[0] ?? 'document';
              const isImage = /\.(jpg|jpeg|png|webp)(\?|$)/i.test(att.url);
              return (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5"
                >
                  <FontAwesomeIcon
                    icon={isImage ? faImage : faFile}
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  />
                  <a
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 truncate text-xs text-primary hover:underline"
                  >
                    {name}
                  </a>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() =>
                        set({ removedAttachmentIndices: [...state.removedAttachmentIndices, i] })
                      }
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      aria-label="Remove attachment"
                    >
                      <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
            {state.pendingAttachmentFiles.map((f, i) => (
              <div
                key={`pend-${i}`}
                className="flex items-center gap-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-3 py-1.5"
              >
                <FontAwesomeIcon icon={faFile} className="h-3.5 w-3.5 shrink-0 text-primary/60" />
                <span className="flex-1 truncate text-xs text-primary/80">{f.name}</span>
                <span className="rounded bg-primary/10 px-1.5 text-[10px] font-medium text-primary/60">
                  NEW
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() =>
                      set({
                        pendingAttachmentFiles: state.pendingAttachmentFiles.filter(
                          (_, idx) => idx !== i,
                        ),
                      })
                    }
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    aria-label="Remove pending attachment"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {totalAttachments < MAX_SHIPMENT_ATTACHMENTS && !disabled && (
          <button
            type="button"
            onClick={() => attachInputRef.current?.click()}
            className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:underline"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
            Attach document
          </button>
        )}
        <input
          ref={attachInputRef}
          type="file"
          accept={ATTACHMENT_MIME}
          multiple
          className="sr-only"
          onChange={handleAttachChange}
        />
      </div>
    </div>
  );
}
